import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, merge } from 'rxjs';
import { MediaFolder, sortFolderByName, FolderNode } from './common-classes';
import { FlatTreeControl } from '@angular/cdk/tree';
import { UtilsService } from '../services/Utils/utils.service';
import { CollectionViewer, SelectionChange } from '@angular/cdk/collections';
import { map } from 'rxjs/operators';
import { FolderService } from '../services/Folder/folder.service';


/**
 * File database, it can build a tree structured Json object from string.
 * Each node in Json object represents a file or a directory. For a file, it has filename and type.
 * For a directory, it has filename and children (a list of files or directories).
 * The input will be a json object string, and the output is a list of `FileNode` with nested
 * structure.
 */
@Injectable()
export class FolderDataSource {
    dataChange: BehaviorSubject<FolderNode[]> = new BehaviorSubject<FolderNode[]>([]);

    get data(): FolderNode[] { return this.dataChange.value; }
    set data(value: FolderNode[]) {
        this.treeControl.dataNodes = value;
        this.dataChange.next(value);
    }

    constructor(private treeControl: FlatTreeControl<FolderNode>,
        private mediaTreeId: string,
        public utils: UtilsService,
        private folderService: FolderService) {
        //inizializzo le cartelle del DataSource
        folderService.getFolders(null, mediaTreeId).then((folders: Array<MediaFolder>) => {
            folders.forEach((folder) => {
                let folderNode: FolderNode = FolderNode.fromMediaFolder(folder, 0);
                this.data.push(folderNode);
            });
            this.dataChange.next(this.data);
        }).catch((err) => {
            console.error(err);
        });
    }

    connect(collectionViewer: CollectionViewer): Observable<FolderNode[]> {
        this.treeControl.expansionModel.onChange!.subscribe(change => {
            if ((change as SelectionChange<FolderNode>).added ||
                (change as SelectionChange<FolderNode>).removed) {
                this.handleTreeControl(change as SelectionChange<FolderNode>);
            }
        });

        return merge(collectionViewer.viewChange, this.dataChange).pipe(map(() => this.data));
    }

    public addFolder(newFolder: MediaFolder, node: FolderNode): void {
        let _self = this;
        const index = _self.data.indexOf(node);
        if (!node) {
            //il nodo è null aggiungo alla root
            let folder: FolderNode = FolderNode.fromMediaFolder(newFolder, 0);
            //prendo tutte le cartelle al livello 0
            let rootFolders = _self.data.filter((el: FolderNode) => {
                return el.level == 0;
            });
            rootFolders.push(folder);
            rootFolders.sort(sortFolderByName);
            let i: number = rootFolders.indexOf(folder);
            let index: number = 0;
            if (i > 0) {
                index = _self.data.indexOf(rootFolders[i - 1]);
                if (index > -1) {
                    let tmpNode: FolderNode = _self.data[index];
                    let children: Array<FolderNode> = _self.getChildren(tmpNode.mediaFolderId) || [];
                    index = index + children.length + 1;
                } else {
                    index = 0;
                }
            }
            _self.data.splice(index, 0, folder);
        } else if (index > -1) {
            //il nodo è presente e si trova attualmente nell' albero: aggiungo
            let folder: FolderNode = FolderNode.fromMediaFolder(newFolder, node.level + 1);
            let children: Array<FolderNode> = _self.getChildren(node.mediaFolderId) || [];
            children.push(folder);
            children.sort(sortFolderByName);
            let i: number = children.indexOf(folder);
            if (i > -1) {
                _self.data.splice(index + i + 1, 0, folder);
            } else {
                _self.data.splice(index + 1, 0, folder);
            }
            node.expandable = true;
        }
        _self.refreshTree();
    }

    public deleteFolder(node: FolderNode): void {
        let _self = this;
        _self.closeFolder(node);
        _self.refreshTree();
    }

    public handleTreeControl(change: SelectionChange<FolderNode>): void {
        if (change.added) {
            change.added.forEach((node) => {
                this.toggleNode(node, true)
            });
        }
        if (change.removed) {
            change.removed.reverse().forEach((node) => {
                this.toggleNode(node, false)
            });
        }
    }

    public isChildren(parentNode: FolderNode, testNode: FolderNode): boolean {
        let _self = this;
        let isChild = false;

        if (testNode.mediaFolderId === parentNode.mediaFolderId) {
            isChild = true;
        }

        while (testNode = _self.getParentNode(testNode.parentFolderId)) {
            if (testNode.mediaFolderId == parentNode.mediaFolderId) {
                isChild = true;
            }
        }

        return isChild;
    }

    /*public folderContainsName(name: string, testNode: DynamicFolderNode): boolean {
        let _self = this;
        let containsName: boolean = false;

        _self.getChildren(testNode.mediaFolderId).forEach((node: DynamicFolderNode) => {
            if (name == node.name) {
                containsName = true;
            }
        })

        return containsName;
    } */

    private toggleNode(node: FolderNode, expand: boolean): void {
        let _self = this;
        const index = this.data.indexOf(node);
        if (expand) {
            node.isLoading = true;
            _self.utils.showShortLoader();
            _self.folderService.getFolders(node.mediaFolderId, _self.mediaTreeId).then((folders: Array<MediaFolder>) => {
                let nodes: Array<FolderNode> = [];
                folders.forEach((folder) => {
                    let folderNode: FolderNode = FolderNode.fromMediaFolder(folder, node.level + 1);
                    nodes.push(folderNode);
                });

                if (index < 0 || nodes.length == 0) {
                    node.expandable = false;
                    this.refreshTree();
                    return;
                }
                this.data.splice(index + 1, 0, ...nodes);
                this.dataChange.next(this.data);
            }).catch((err) => {
                return _self.utils.getErrorShowToast(err);
            }).finally(() => {
                node.isLoading = false;
                _self.utils.hideShortLoader();
            });
        } else {
            let children: Array<FolderNode> = _self.getChildren(node.mediaFolderId);
            if (children && children.length > 0) {
                children.forEach((child) => {
                    _self.closeFolder(child);
                });
                this.dataChange.next(this.data);
            }
        }
    }

    private closeFolder(folder: FolderNode): void {
        let _self = this;
        let children: Array<FolderNode> = _self.getChildren(folder.mediaFolderId);
        if (children && children.length > 0) {
            children.forEach((child) => {
                _self.closeFolder(child);
            });
        }

        const index = _self.data.indexOf(folder);
        if (index > -1) {
            _self.data.splice(index, 1);
        }
    }

    private refreshTree(): void {
        let _self = this;
        let _data = _self.data;
        _self.data = null;
        _self.data = _data;
    }

    private getChildren(mediaFolderId: string): Array<FolderNode> {
        let _self = this;
        let children: Array<FolderNode> = _self.data.filter((node: FolderNode) => {
            return node.parentFolderId == mediaFolderId;
        });

        return children;
    }

    private getParentNode(parentFolderId: string): FolderNode {
        let _self = this;
        let parent: FolderNode = _self.data.find((node: FolderNode) => {
            return node.mediaFolderId == parentFolderId;
        });
        return parent;
    }

    public getFolder(mediaFolderId: string): FolderNode {
        let _self = this;
        return _self.data.find((node: FolderNode) => {
            return node.mediaFolderId == mediaFolderId;
        });
    }

    public updateFolder(updatedFolder: FolderNode): void {
        let _self = this;
        let folder: FolderNode = _self.getFolder(updatedFolder.mediaFolderId);
        let folderIndex: number = _self.data.indexOf(folder);
        if (folderIndex > -1) {
            _self.data.splice(folderIndex, 1, updatedFolder);
        }
        _self.refreshTree();
    }
}